import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  giftCouponThemeListDataAtom,
  giftCouponThemeListStatusAtom,
} from '@shared/api/giftCouponTheme/giftCouponTheme.atom';
import {
  deleteGiftCouponTheme,
  updateGiftCouponTheme,
} from '@shared/api/giftCouponTheme/giftCouponTheme.controller';
import { useGiftCouponThemeListHook } from '@shared/api/giftCouponTheme/giftCouponTheme.hook';
import { GiftCouponTheme } from '@shared/api/giftCouponTheme/giftCouponTheme.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';

export const GiftCouponThemeListPage: FC = function GiftCouponThemeListPage() {
  const { sellerRole } = useAuth();

  useGiftCouponThemeListHook(sellerRole());

  const status = useRecoilValue(giftCouponThemeListStatusAtom);
  const data = useRecoilValue(giftCouponThemeListDataAtom);
  const setData = useSetRecoilState(giftCouponThemeListDataAtom);

  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase().trim();
    return data.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.code.toLowerCase().includes(query),
    );
  }, [data, searchQuery]);

  // 활성화 토글 핸들러
  const handleToggleEnabled = useCallback(
    async (theme: GiftCouponTheme) => {
      try {
        setTogglingId(theme._id);
        const updated = await updateGiftCouponTheme(
          theme._id,
          { isEnabled: !theme.isEnabled },
          sellerRole(),
        );
        setData(
          (prev) =>
            prev?.map((item) => (item._id === theme._id ? updated : item)) ?? null,
        );
      } catch (error) {
        console.error(error);
        alert('상태 변경에 실패했습니다.');
      } finally {
        setTogglingId(null);
      }
    },
    [sellerRole, setData],
  );

  // 삭제 핸들러
  const handleDelete = useCallback(
    async (theme: GiftCouponTheme) => {
      if (!confirm(`"${theme.name}" 테마를 삭제하시겠습니까?`)) return;

      try {
        setDeletingId(theme._id);
        await deleteGiftCouponTheme(theme._id, sellerRole());
        setData((prev) => prev?.filter((item) => item._id !== theme._id) ?? null);
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData],
  );

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {searchQuery ? '검색 결과가 없습니다.' : '등록된 테마가 없습니다.'}
          </div>
        );
      }

      return (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {filteredData.map((item) => (
            <div
              key={item._id}
              className={`relative overflow-hidden rounded-xl border bg-white shadow-sm transition ${
                item.isEnabled
                  ? 'border-indigo-200 ring-1 ring-indigo-200'
                  : 'border-gray-200 opacity-60'
              }`}
            >
              {/* 이미지 */}
              <div className="aspect-[3/4] bg-gray-100">
                {item.image?.url ? (
                  <img
                    src={fileBucketUrl(item.image.url, 'w400')}
                    alt={item.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-gray-400">
                    No Image
                  </div>
                )}
              </div>

              {/* 정보 */}
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-xs text-gray-500">{item.code}</p>
                  </div>
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    #{item.order}
                  </span>
                </div>

                {/* 활성화 토글 */}
                <div className="mt-2 flex items-center justify-between">
                  <button
                    onClick={() => handleToggleEnabled(item)}
                    disabled={togglingId === item._id}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                      item.isEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                    } ${togglingId === item._id ? 'opacity-50' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        item.isEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-xs text-gray-500">
                    {item.isEnabled ? '활성화' : '비활성화'}
                  </span>
                </div>

                {/* 버튼 */}
                <div className="mt-3 flex gap-2">
                  <Link
                    to={`/gift-seller/coupon-theme/${item._id}/edit`}
                    className="flex-1 rounded-lg border border-gray-300 py-1.5 text-center text-xs text-gray-600 hover:bg-gray-100"
                  >
                    수정
                  </Link>
                  <button
                    onClick={() => handleDelete(item)}
                    disabled={deletingId === item._id}
                    className="flex-1 rounded-lg border border-red-300 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {deletingId === item._id ? '...' : '삭제'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [
    status,
    filteredData,
    searchQuery,
    deletingId,
    togglingId,
    handleDelete,
    handleToggleEnabled,
  ]);

  // 통계
  const stats = useMemo(() => {
    if (!data) return { total: 0, enabled: 0 };
    return {
      total: data.length,
      enabled: data.filter((item) => item.isEnabled).length,
    };
  }, [data]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">교환권 테마 관리</h1>
          <Link to="/gift-seller/coupon-theme/new">
            <Button className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white">
              + 테마 등록
            </Button>
          </Link>
        </div>

        {/* 통계 */}
        {status === 'success' && (
          <div className="mt-3 flex gap-4 text-sm text-gray-600">
            <span>
              전체 <span className="font-medium text-gray-900">{stats.total}</span>개
            </span>
            <span>
              활성화 <span className="font-medium text-indigo-600">{stats.enabled}</span>
              개
            </span>
          </div>
        )}
      </div>

      {/* 검색 */}
      <div className="border-b bg-white px-6 py-3">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="테마명 또는 코드 검색..."
            className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            🔍
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
